import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  ADMIN_EVENT_TYPES,
  MONTH_NAMES,
  calculateEndTime,
  compareEventsByTime,
  formatDayHeadingFromDateStr,
  getEventTypeById,
} from '../../utils/calendarHelpers';
import { formatEventConflictWarnings } from '../../utils/formatEventConflictWarning';
import '../pages/styles/AdminPanel.css';

const CELL_PREVIEW_MAX = 3;
const WEEKDAY_OPTIONS = [
  { id: 0, label: 'H' },
  { id: 1, label: 'K' },
  { id: 2, label: 'Sze' },
  { id: 3, label: 'Cs' },
  { id: 4, label: 'P' },
  { id: 5, label: 'Szo' },
  { id: 6, label: 'V' },
];

const RECURRENCE_LABELS = {
  daily: 'Napi',
  weekly: 'Heti',
  biweekly: 'Kéthetente',
  monthly: 'Havi',
};

const usesWeekdayPicker = (recurrenceType) =>
  recurrenceType === 'weekly' || recurrenceType === 'biweekly';

const WEEKDAY_HU = ['Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat', 'Vasárnap'];

function buildEventDetail(form, duration) {
  const parts = [];

  const typeObj = typeof form.type === 'string'
    ? { id: form.type }
    : null;
  if (form.type) parts.push(`Típus: ${form.type}`);

  if (form.startTime) {
    const end = form.endTime || '';
    parts.push(`Idő: ${form.startTime}${end ? `–${end}` : ''}`);
  }

  if (duration) parts.push(`Időtartam: ${duration} perc`);

  if (form.recurrenceType && form.recurrenceType !== 'once') {
    const freqLabel = RECURRENCE_LABELS[form.recurrenceType] || form.recurrenceType;
    let recStr = `Ismétlés: ${freqLabel}`;
    if (usesWeekdayPicker(form.recurrenceType) && form.daysOfWeek?.length) {
      recStr += ` (${form.daysOfWeek.map((d) => WEEKDAY_HU[d]).join(', ')})`;
    }
    if (form.recurrenceEndDate) recStr += `, vége: ${form.recurrenceEndDate}`;
    parts.push(recStr);
  } else {
    parts.push('Egyszeri esemény');
  }

  if (form.description) parts.push(`Leírás: ${String(form.description).slice(0, 80)}`);

  return parts.join(' | ');
}

function buildDeleteDetail(event) {
  const parts = [];
  if (event.time) parts.push(`Idő: ${event.time}`);
  if (event.duration) parts.push(`Időtartam: ${event.duration} perc`);
  if (event.recurrenceFrequency) {
    parts.push(`Ismétlés: ${RECURRENCE_LABELS[event.recurrenceFrequency] || event.recurrenceFrequency}`);
  } else {
    parts.push('Egyszeri esemény');
  }
  return parts.join(' | ') || null;
}

function formatTimeShort(time) {
  if (!time) return '';
  return time.substring(0, 5);
}

function defaultRecurrenceEndDate(fromDate = new Date()) {
  const d = new Date(fromDate);
  d.setMonth(d.getMonth() + 3);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function validateDuration(startTime, endTime) {
  if (!startTime || !endTime) {
    return { ok: false, error: 'Kérjük, adjon meg kezdési és befejezési időt!' };
  }
  const [startHours, startMinutes] = startTime.split(':').map(Number);
  const [endHours, endMinutes] = endTime.split(':').map(Number);
  const startTotal = startHours * 60 + startMinutes;
  const endTotal = endHours * 60 + endMinutes;
  if (endTotal <= startTotal) {
    return { ok: false, error: 'A befejezés időpontja nem lehet korábbi, mint a kezdés.' };
  }
  const duration = endTotal - startTotal;
  if (duration < 15) {
    return { ok: false, error: 'Az esemény időtartama legalább 15 perc legyen.' };
  }
  if (duration > 480) {
    return { ok: false, error: 'Az esemény időtartama legfeljebb 8 óra lehet.' };
  }
  return { ok: true, duration };
}

const ACTION_LABELS = {
  create: 'Létrehozás',
  update: 'Frissítés',
  delete: 'Törlés',
  copy: 'Másolás',
  validation_error: 'Validációs hiba',
  backend_error: 'Szerver hiba',
};

const AdminCalendarManager = ({ apiCall, showToast, currentUser }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [viewMode, setViewMode] = useState('month');
  const [editingEvent, setEditingEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editScope, setEditScope] = useState('series');
  const [scopeModal, setScopeModal] = useState(null);
  const [deleteConfirmEvent, setDeleteConfirmEvent] = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [logEntries, setLogEntries] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logFilter, setLogFilter] = useState('all');
  const logSilentRef = useRef(true);

  const [newEvent, setNewEvent] = useState({
    title: '',
    date: '',
    startTime: '',
    endTime: '',
    type: 'meal',
    description: '',
    recurrenceType: 'once',
    daysOfWeek: [],
    recurrenceEndDate: '',
  });

  const emptyEventForm = () => ({
    title: '',
    date: '',
    startTime: '',
    endTime: '',
    type: 'meal',
    description: '',
    recurrenceType: 'once',
    daysOfWeek: [],
    recurrenceEndDate: '',
  });

  const yearOptions = useMemo(() => {
    const y = new Date().getFullYear();
    return Array.from({ length: 11 }, (_, i) => y - 5 + i);
  }, []);

  const getParentId = (event) => {
    if (!event) return null;
    if (event.sourceEventId) return event.sourceEventId;
    if (event.id && String(event.id).includes('::')) {
      return String(event.id).split('::')[0];
    }
    return event.id;
  };

  const isRecurringInstance = (event) =>
    Boolean(event?.isRecurring || event?.sourceEventId);

  const getWeekdayFromDateStr = (dateStr) => {
    if (!dateStr) return 0;
    const [y, m, d] = dateStr.split('-').map(Number);
    return (new Date(y, m - 1, d).getDay() + 6) % 7;
  };

  const buildRecurringPayload = (form) => {
    if (form.recurrenceType === 'once') {
      return { enabled: false };
    }
    return {
      enabled: true,
      frequency: form.recurrenceType,
      endDate: form.recurrenceEndDate || null,
      daysOfWeek: usesWeekdayPicker(form.recurrenceType) ? form.daysOfWeek : [],
    };
  };

  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startingDay = (firstDay.getDay() + 6) % 7;
    const days = [];
    for (let i = 0; i < startingDay; i += 1) days.push(null);
    for (let day = 1; day <= daysInMonth; day += 1) days.push(day);
    return days;
  };

  const getEventsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events
      .filter((e) => e.date === dateStr)
      .sort(compareEventsByTime);
  };

  const getSelectedDayEvents = () => {
    const dates =
      selectedDates.length > 0
        ? selectedDates
        : selectedDate
          ? [selectedDate]
          : [];
    if (dates.length === 0) return [];
    return events
      .filter((e) => dates.includes(e.date))
      .sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return compareEventsByTime(a, b);
      });
  };

  const dateStrFromDay = (day) =>
    `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  const clearDaySelection = () => {
    setSelectedDates([]);
    setSelectedDate(null);
  };

  const loadEvents = async () => {
    setIsLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const data = await apiCall(`/events/calendar/${year}/${month}`);
      const eventsArray = [];
      Object.entries(data.events || {}).forEach(([date, dayEvents]) => {
        dayEvents.forEach((event) => {
          eventsArray.push({
            ...event,
            date,
            id: event.id || event._id,
          });
        });
      });
      setEvents(eventsArray);
    } catch (error) {
      console.error('Failed to load events:', error);
      setEvents([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, [currentDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const calculateEndTimeFromDuration = (startTime, duration) =>
    calculateEndTime(startTime, duration);

  const fillFormFromEvent = (event, recurrenceMeta = null) => {
    const endTime = calculateEndTimeFromDuration(event.time, event.duration);
    const rec = recurrenceMeta || {};
    setNewEvent({
      title: event.title,
      description: event.description || '',
      date: event.date,
      startTime: event.time,
      endTime,
      type: event.type || 'other',
      recurrenceType: rec.enabled ? rec.frequency : 'once',
      daysOfWeek: rec.daysOfWeek || [],
      recurrenceEndDate: rec.endDate
        ? formatDateForInput(new Date(rec.endDate))
        : '',
    });
  };

  const loadParentTemplate = async (parentId) => {
    const data = await apiCall(`/events/${parentId}`);
    const parent = data.event || data;
    const rec = parent.recurring || {};
    fillFormFromEvent(
      {
        title: parent.title,
        description: parent.description,
        date: formatDateForInput(new Date(parent.date)),
        time: parent.time,
        duration: parent.duration,
        type: parent.type,
      },
      rec
    );
    return parent;
  };

  const openEditForm = async (event, scope) => {
    setEditingEvent(event);
    setEditScope(scope);
    if (scope === 'series' && isRecurringInstance(event)) {
      setIsLoading(true);
      try {
        await loadParentTemplate(getParentId(event));
      } catch (error) {
        showToast('Nem sikerült betölteni a sorozatot: ' + error.message, 'error');
        return;
      } finally {
        setIsLoading(false);
      }
    } else {
      fillFormFromEvent(event);
    }
    setShowEventModal(true);
  };

  const requestEdit = (event) => {
    if (isRecurringInstance(event)) {
      setScopeModal({ mode: 'edit', event });
    } else {
      openEditForm(event, 'series');
    }
  };

  const requestDelete = (event) => {
    if (isRecurringInstance(event)) {
      setScopeModal({ mode: 'delete', event });
    } else {
      setDeleteConfirmEvent(event);
    }
  };

  const executeDelete = async (event, scope) => {
    setIsLoading(true);
    try {
      const parentId = getParentId(event);
      const endpoint =
        scope === 'occurrence'
          ? `/events/${parentId}?scope=occurrence&occurrenceDate=${event.date}`
          : `/events/${parentId}?scope=series`;
      await apiCall(endpoint, 'DELETE');
      logActivity('delete', event.title, event.date, scope, 'success', buildDeleteDetail(event));
      await loadEvents();
      if (showEventModal) {
        setShowEventModal(false);
        setEditingEvent(null);
        setNewEvent(emptyEventForm());
      }
      showToast(
        scope === 'occurrence'
          ? 'Az adott nap előfordulása törölve.'
          : 'Esemény sikeresen törölve!'
      );
    } catch (error) {
      logActivity('backend_error', event.title, event.date, scope, 'error', error.message);
      showToast('Hiba történt az esemény törlésekor: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const logActivity = useCallback(
    (action, eventTitle, eventDate, scope, status, detail) => {
      apiCall('/activity-log', 'POST', {
        action,
        eventTitle: eventTitle || null,
        eventDate: eventDate || null,
        scope: scope || null,
        status,
        detail: detail ? String(detail).slice(0, 500) : null,
      }).catch(() => {});
    },
    [apiCall]
  );

  const openLogModal = useCallback(async () => {
    setShowLogModal(true);
    setLogLoading(true);
    try {
      const data = await apiCall('/activity-log');
      setLogEntries(Array.isArray(data) ? data : []);
    } catch {
      setLogEntries([]);
    } finally {
      setLogLoading(false);
    }
  }, [apiCall]);

  const validateFormBeforeSave = () => {
    const durationResult = validateDuration(newEvent.startTime, newEvent.endTime);
    if (!durationResult.ok) {
      showToast(durationResult.error, 'error');
      logActivity('validation_error', newEvent.title || '(ismeretlen)', newEvent.date || null, null, 'error', durationResult.error);
      return null;
    }
    if (usesWeekdayPicker(newEvent.recurrenceType) && newEvent.daysOfWeek.length === 0) {
      const msg = 'Válasszon legalább egy napot a heti / kétheti ismétlődéshez!';
      showToast(msg, 'error');
      logActivity('validation_error', newEvent.title || '(ismeretlen)', newEvent.date || null, null, 'error', msg);
      return null;
    }
    return durationResult.duration;
  };

  const getCreateTargetDates = () => {
    if (newEvent.recurrenceType !== 'once') {
      return [newEvent.date];
    }
    if (selectedDates.length > 1) {
      return [...selectedDates].sort();
    }
    return [newEvent.date];
  };

  const collectWarnings = (responses) => {
    const merged = [];
    const seen = new Set();
    responses.forEach((response) => {
      (response?.warnings || []).forEach((warning) => {
        const item = {
          date: warning.date,
          time: warning.time,
          otherTitle: warning.otherTitle,
        };
        const key = `${item.date}|${item.time}|${item.otherTitle}`;
        if (seen.has(key)) return;
        seen.add(key);
        merged.push(item);
      });
    });
    return merged;
  };

  const showConflictWarnings = (warnings) => {
    if (!warnings.length) return;
    const text = formatEventConflictWarnings(warnings);
    showToast(text, 'warning', 6500);
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    const duration = validateFormBeforeSave();
    if (duration == null) return;

    const targetDates = getCreateTargetDates();
    if (!targetDates[0]) {
      showToast('Jelöljön ki legalább egy napot a naptárban.', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        title: newEvent.title,
        description: newEvent.description,
        time: newEvent.startTime,
        duration,
        type: newEvent.type,
        recurring: buildRecurringPayload(newEvent),
      };

      const responses = [];
      for (const date of targetDates) {
        const response = await apiCall('/events', 'POST', { ...payload, date });
        responses.push(response);
      }

      const warnings = collectWarnings(responses);

      const scopeLabel = newEvent.recurrenceType !== 'once' ? 'series' : 'one-time';
      const createDetail = buildEventDetail(newEvent, duration);
      targetDates.forEach((date) => {
        logActivity('create', newEvent.title, date, scopeLabel, 'success', createDetail);
      });

      await loadEvents();
      setNewEvent(emptyEventForm());
      setShowEventModal(false);
      if (targetDates.length > 1) {
        showToast(`${targetDates.length} esemény sikeresen hozzáadva a kijelölt napokra.`);
      } else {
        showToast('Esemény sikeresen hozzáadva!');
      }
      showConflictWarnings(warnings);
    } catch (error) {
      logActivity('backend_error', newEvent.title, newEvent.date, null, 'error', error.message);
      showToast('Hiba történt az esemény hozzáadásakor: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    const duration = validateFormBeforeSave();
    if (duration == null) return;
    setIsLoading(true);
    try {
      const parentId = getParentId(editingEvent);
      const isOccurrenceEdit =
        isRecurringInstance(editingEvent) && editScope === 'occurrence';
      const eventData = isOccurrenceEdit
        ? {
            scope: 'occurrence',
            occurrenceDate: editingEvent.date,
            title: newEvent.title,
            description: newEvent.description,
            time: newEvent.startTime,
            duration,
            type: newEvent.type,
          }
        : {
            scope: 'series',
            title: newEvent.title,
            description: newEvent.description,
            date: newEvent.date,
            time: newEvent.startTime,
            duration,
            type: newEvent.type,
            recurring: buildRecurringPayload(newEvent),
          };
      const response = await apiCall(`/events/${parentId}`, 'PUT', eventData);
      const updateScope = isOccurrenceEdit ? 'occurrence' : 'series';
      const updateDetail = buildEventDetail(newEvent, duration);
      logActivity('update', newEvent.title, newEvent.date || editingEvent?.date, updateScope, 'success', updateDetail);
      await loadEvents();
      setEditingEvent(null);
      setNewEvent(emptyEventForm());
      setShowEventModal(false);
      showToast('Esemény sikeresen frissítve!');
      showConflictWarnings(collectWarnings([response]));
    } catch (error) {
      logActivity('backend_error', newEvent.title, newEvent.date || editingEvent?.date, null, 'error', error.message);
      showToast('Hiba történt az esemény frissítésekor: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyEvent = async (event) => {
    const targetDate = selectedDate || event.date;
    const duration = event.duration || 60;
    setIsLoading(true);
    try {
      await apiCall('/events', 'POST', {
        title: event.title,
        description: event.description || '',
        date: targetDate,
        time: event.time,
        duration,
        type: event.type,
        recurring: { enabled: false },
      });
      const copyDetail = buildDeleteDetail({ ...event, date: targetDate });
      logActivity('copy', event.title, targetDate, 'one-time', 'success', copyDetail);
      await loadEvents();
      showToast('Esemény másolva erre a napra.');
    } catch (error) {
      logActivity('backend_error', event.title, targetDate, 'one-time', 'error', error.message);
      showToast('Másolás sikertelen: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const navigateMonth = (direction) => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1)
    );
  };

  const goToToday = () => {
    const today = new Date();
    const todayStr = formatDateForInput(today);
    setCurrentDate(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedDate(todayStr);
    setSelectedDates(multiSelectMode ? [todayStr] : [todayStr]);
  };

  const handleMonthSelect = (monthIndex) => {
    setCurrentDate(new Date(currentDate.getFullYear(), Number(monthIndex), 1));
  };

  const handleYearSelect = (year) => {
    setCurrentDate(new Date(Number(year), currentDate.getMonth(), 1));
  };

  const openNewEventModal = () => {
    setEditingEvent(null);
    setEditScope('series');

    const dates =
      selectedDates.length > 0
        ? selectedDates
        : selectedDate
          ? [selectedDate]
          : [];

    if (dates.length === 0) {
      showToast('Jelöljön ki legalább egy napot a naptárban (kattintson egy napra).', 'error');
      return;
    }

    const defaultDate = dates[0];
    setNewEvent({
      ...emptyEventForm(),
      date: defaultDate,
      daysOfWeek: [getWeekdayFromDateStr(defaultDate)],
      recurrenceEndDate: defaultRecurrenceEndDate(),
    });
    setShowEventModal(true);
  };

  const toggleWeekday = (dayId) => {
    setNewEvent((prev) => {
      const has = prev.daysOfWeek.includes(dayId);
      const daysOfWeek = has
        ? prev.daysOfWeek.filter((d) => d !== dayId)
        : [...prev.daysOfWeek, dayId].sort((a, b) => a - b);
      return { ...prev, daysOfWeek };
    });
  };

  const handleDayClick = (day) => {
    const clickedDate = dateStrFromDay(day);

    if (multiSelectMode) {
      setSelectedDates((prev) => {
        if (prev.includes(clickedDate)) {
          const next = prev.filter((d) => d !== clickedDate);
          setSelectedDate(next.length ? next[next.length - 1] : null);
          return next;
        }
        const next = [...prev, clickedDate].sort();
        setSelectedDate(clickedDate);
        return next;
      });
      return;
    }

    setSelectedDates([clickedDate]);
    setSelectedDate(clickedDate);
  };

  const toggleMultiSelectMode = () => {
    setMultiSelectMode((on) => {
      const next = !on;
      if (next && selectedDate && selectedDates.length === 0) {
        setSelectedDates([selectedDate]);
      }
      if (!next && selectedDates.length > 0) {
        const last = selectedDates[selectedDates.length - 1];
        setSelectedDates([last]);
        setSelectedDate(last);
      }
      return next;
    });
  };

  const handleCellEventClick = (e, event, day) => {
    e.stopPropagation();
    handleDayClick(day);
    requestEdit(event);
  };

  const handleEventDateChange = (dateValue) => {
    setNewEvent((prev) => {
      const next = { ...prev, date: dateValue };
      if (usesWeekdayPicker(prev.recurrenceType) && dateValue && prev.daysOfWeek.length === 0) {
        next.daysOfWeek = [getWeekdayFromDateStr(dateValue)];
      }
      return next;
    });
    if (dateValue) {
      setSelectedDate(dateValue);
      if (!multiSelectMode) {
        setSelectedDates([dateValue]);
      }
    }
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const isSelectedDay = (day) => {
    if (!day) return false;
    const dayDate = dateStrFromDay(day);
    if (selectedDates.length > 0) {
      return selectedDates.includes(dayDate);
    }
    return selectedDate === dayDate;
  };

  const hasDaySelection = selectedDates.length > 0 || Boolean(selectedDate);
  const selectionCount = selectedDates.length || (selectedDate ? 1 : 0);
  const useMultiDateCreate =
    !editingEvent && newEvent.recurrenceType === 'once' && selectedDates.length > 1;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  const dayNames = ['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'];
  const selectedDayEvents = getSelectedDayEvents();

  return (
    <div className="calendar-manager">
      <div className="calendar-manager-header">
        <h2>Naptár kezelése</h2>
        <div className="calendar-actions">
          <button type="button" className="add-event-btn" onClick={openNewEventModal}>
            + Új esemény
          </button>
          <div className="view-toggles">
            <button
              type="button"
              className={`view-btn ${viewMode === 'month' ? 'active' : ''}`}
              onClick={() => setViewMode('month')}
            >
              Hónap
            </button>
            <button
              type="button"
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              Lista
            </button>
          </div>
        </div>
      </div>

      {viewMode === 'month' ? (
        <div className="calendar-manager-layout">
          <div className="calendar-manager-main">
            <div className="calendar-toolbar">
              <div className="calendar-toolbar-nav">
                <button type="button" className="nav-btn" onClick={() => navigateMonth(-1)}>
                  Előző hónap
                </button>
                <button type="button" className="nav-btn nav-btn--ghost" onClick={goToToday}>
                  Mai nap
                </button>
                <button type="button" className="nav-btn" onClick={() => navigateMonth(1)}>
                  Következő hónap
                </button>
                <button
                  type="button"
                  className={`calendar-multi-select-btn ${multiSelectMode ? 'is-active' : ''}`}
                  onClick={toggleMultiSelectMode}
                  aria-pressed={multiSelectMode}
                >
                  <span className="calendar-multi-select-btn__icon" aria-hidden="true">
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 40 40"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="6"
                        y="9"
                        width="28"
                        height="26"
                        rx="3"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path d="M6 15h28" stroke="currentColor" strokeWidth="2" />
                      <path
                        d="M13 6v6M27 6v6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <path
                        d="M10 28h20"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeDasharray="3 3"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                  <span className="calendar-multi-select-btn__text">
                    <span className="calendar-multi-select-btn__title">Több nap kijelölése</span>
                    <span className="calendar-multi-select-btn__subtitle">
                      Dátumtartomány kijelölése
                    </span>
                  </span>
                </button>
                {selectionCount > 0 && (
                  <button
                    type="button"
                    className="nav-btn nav-btn--ghost"
                    onClick={clearDaySelection}
                  >
                    Kijelölés törlése
                  </button>
                )}
              </div>
              <div className="calendar-toolbar-pickers">
                <select
                  value={currentDate.getMonth()}
                  onChange={(e) => handleMonthSelect(e.target.value)}
                  aria-label="Hónap"
                >
                  {MONTH_NAMES.map((name, idx) => (
                    <option key={name} value={idx}>
                      {name}
                    </option>
                  ))}
                </select>
                <select
                  value={currentDate.getFullYear()}
                  onChange={(e) => handleYearSelect(e.target.value)}
                  aria-label="Év"
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <h3 className="calendar-toolbar-title">
                {currentDate.getFullYear()} {MONTH_NAMES[currentDate.getMonth()].toLowerCase()}
              </h3>
            </div>

            {multiSelectMode && (
              <p className="calendar-multi-select-hint">
                Kattintson a napokra a kijelöléshez. Ezután: + Új esemény (egyszeri) a kijelölt napokra.
                {selectionCount > 0 && (
                  <strong> {selectionCount} nap kijelölve.</strong>
                )}
              </p>
            )}

            {hasDaySelection && !multiSelectMode && selectedDate && (
              <p className="calendar-selection-banner">
                Kiválasztott nap: <strong>{formatDate(selectedDate)}</strong>
              </p>
            )}

            {isLoading && <p className="calendar-loading-hint">Betöltés…</p>}

            <div className={`admin-calendar-grid ${multiSelectMode ? 'admin-calendar-grid--multi' : ''}`}>
              <div className="admin-day-headers">
                {dayNames.map((day) => (
                  <div key={day} className="admin-day-header">
                    {day}
                  </div>
                ))}
              </div>
              <div className="admin-calendar-days">
                {getDaysInMonth(currentDate).map((day, index) => {
                  const dayEvents = day ? getEventsForDay(day) : [];
                  const preview = dayEvents.slice(0, CELL_PREVIEW_MAX);
                  const moreCount = dayEvents.length - CELL_PREVIEW_MAX;
                  return (
                    <div
                      key={index}
                      className={`admin-calendar-day ${day ? 'has-date' : 'empty'} ${dayEvents.length > 0 ? 'has-events' : ''} ${isToday(day) ? 'today' : ''} ${isSelectedDay(day) ? 'selected' : ''}`}
                      onClick={() => day && handleDayClick(day)}
                      onKeyDown={(e) => {
                        if (day && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          handleDayClick(day);
                        }
                      }}
                      role={day ? 'button' : undefined}
                      tabIndex={day ? 0 : undefined}
                    >
                      {day && (
                        <>
                          <span className="admin-day-number">{day}</span>
                          <div className="admin-events-preview">
                            {preview.map((event) => {
                              const meta = getEventTypeById(event.type);
                              return (
                                <button
                                  key={event.id}
                                  type="button"
                                  className="admin-event-preview-row"
                                  onClick={(e) => handleCellEventClick(e, event, day)}
                                >
                                  <span
                                    className="admin-event-preview-dot"
                                    style={{ backgroundColor: meta.color }}
                                  />
                                  <span className="admin-event-preview-time">
                                    {formatTimeShort(event.time)}
                                  </span>
                                  <span className="admin-event-preview-title">
                                    {event.title}
                                  </span>
                                </button>
                              );
                            })}
                            {moreCount > 0 && (
                              <span className="more-events-indicator">
                                +{moreCount} további
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="calendar-type-legend">
              {ADMIN_EVENT_TYPES.map((type) => (
                <span key={type.id} className="calendar-legend-item">
                  <span className="calendar-legend-dot" style={{ backgroundColor: type.color }} />
                  {type.name}
                </span>
              ))}
            </div>
            <div className="calendar-log-bar">
              <button
                type="button"
                className="calendar-log-btn"
                onClick={openLogModal}
              >
                📋 Tevékenységnapló
              </button>
            </div>
          </div>

          <aside className="calendar-day-sidebar">
            {hasDaySelection ? (
              <>
                <div className="panel-header">
                  {selectionCount > 1 ? (
                    <>
                      <h4>{selectionCount} nap kijelölve</h4>
                      <ul className="selected-dates-list">
                        {selectedDates.map((d) => (
                          <li key={d}>{formatDate(d)}</li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <h4>{formatDayHeadingFromDateStr(selectedDate || selectedDates[0])}</h4>
                  )}
                </div>
                <div className="day-events-list">
                  {selectedDayEvents.length === 0 ? (
                    <p className="day-events-empty">Nincs program ezen a napon.</p>
                  ) : (
                    selectedDayEvents.map((event) => {
                      const meta = getEventTypeById(event.type);
                      const end = calculateEndTime(event.time, event.duration);
                      return (
                        <div key={event.id} className="day-event-item">
                          <span
                            className="day-event-type-dot"
                            style={{ backgroundColor: meta.color }}
                            title={meta.name}
                          />
                          <div className="event-details">
                            <p className="day-event-time-range">
                              {formatTimeShort(event.time)}–{end}
                            </p>
                            <h5 className="day-event-title">{event.title}</h5>
                            <span
                              className="event-type-pill"
                              style={{
                                backgroundColor: `${meta.color}22`,
                                color: meta.color,
                              }}
                            >
                              {meta.icon} {meta.name}
                            </span>
                            {event.isRecurring && event.recurrenceFrequency && (
                              <span className="admin-recurrence-badge">
                                {RECURRENCE_LABELS[event.recurrenceFrequency] ||
                                  event.recurrenceFrequency}
                              </span>
                            )}
                          </div>
                          <div className="event-actions event-actions--labeled">
                            <button
                              type="button"
                              className="event-action-btn"
                              onClick={() => requestEdit(event)}
                            >
                              Szerkesztés
                            </button>
                            <button
                              type="button"
                              className="event-action-btn event-action-btn--danger"
                              onClick={() => requestDelete(event)}
                            >
                              Törlés
                            </button>
                            <button
                              type="button"
                              className="event-action-btn"
                              onClick={() => handleCopyEvent(event)}
                            >
                              Másolás
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <button type="button" className="add-to-day-btn" onClick={openNewEventModal}>
                    {selectionCount > 1
                      ? '+ Új esemény a kijelölt napokra'
                      : '+ Új esemény erre a napra'}
                  </button>
                </div>
                <div className="calendar-recurrence-tip">
                  Tipp: Az ismétlődő események szerkesztésénél a módosítás hatálya kiválasztható.
                </div>
              </>
            ) : (
              <p className="sidebar-placeholder">
                Válasszon egy napot a naptárból az események megtekintéséhez.
              </p>
            )}
          </aside>
        </div>
      ) : (
        <div className="list-view">
          <div className="events-list-header">
            <h3>Összes esemény</h3>
          </div>
          <div className="events-list">
            {events
              .sort(
                (a, b) =>
                  new Date(`${a.date}T${a.time}`) - new Date(`${b.date}T${b.time}`)
              )
              .map((event) => {
                const meta = getEventTypeById(event.type);
                return (
                  <div key={event.id} className="list-event-item">
                    <div className="event-date-badge">
                      <span className="date-day">{new Date(event.date).getDate()}</span>
                      <span className="date-month">
                        {MONTH_NAMES[new Date(event.date).getMonth()].substring(0, 3)}
                      </span>
                    </div>
                    <div className="event-info">
                      <div className="event-header">
                        <h4>
                          {meta.icon} {event.title}
                        </h4>
                        <span
                          className="event-type-badge"
                          style={{ backgroundColor: meta.color }}
                        >
                          {meta.name}
                        </span>
                      </div>
                      <div className="event-meta">
                        <span>{formatTimeShort(event.time)}</span>
                        <span>{event.duration} perc</span>
                        <span>{formatDate(event.date)}</span>
                      </div>
                    </div>
                    <div className="event-actions event-actions--labeled">
                      <button
                        type="button"
                        className="event-action-btn"
                        onClick={() => requestEdit(event)}
                      >
                        Szerkesztés
                      </button>
                      <button
                        type="button"
                        className="event-action-btn event-action-btn--danger"
                        onClick={() => requestDelete(event)}
                      >
                        Törlés
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {scopeModal && (
        <div
          className="scope-modal-overlay"
          onClick={() => setScopeModal(null)}
          role="presentation"
        >
          <div
            className="scope-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="scope-modal-title"
          >
            <h3 id="scope-modal-title">
              {scopeModal.mode === 'delete'
                ? 'Ismétlődő esemény törlése'
                : 'Ismétlődő esemény szerkesztése'}
            </h3>
            <p>
              Ez az esemény egy ismétlődő sorozat része ({scopeModal.event.date}). Csak ezt az
              alkalmat, vagy a teljes sorozatot?
            </p>
            <div className="scope-modal-actions">
              <button
                type="button"
                className="scope-modal-btn scope-modal-btn--primary"
                onClick={async () => {
                  const { event, mode } = scopeModal;
                  setScopeModal(null);
                  if (mode === 'delete') {
                    await executeDelete(event, 'occurrence');
                  } else {
                    await openEditForm(event, 'occurrence');
                  }
                }}
              >
                Csak ezt az alkalmat
              </button>
              <button
                type="button"
                className="scope-modal-btn scope-modal-btn--secondary"
                onClick={async () => {
                  const { event, mode } = scopeModal;
                  setScopeModal(null);
                  if (mode === 'delete') {
                    await executeDelete(event, 'series');
                  } else {
                    await openEditForm(event, 'series');
                  }
                }}
              >
                {scopeModal.mode === 'delete' ? 'Teljes sorozat törlése' : 'Teljes sorozat'}
              </button>
              <button
                type="button"
                className="scope-modal-btn scope-modal-btn--ghost"
                onClick={() => setScopeModal(null)}
              >
                Mégse
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirmEvent && (
        <div
          className="scope-modal-overlay"
          onClick={() => setDeleteConfirmEvent(null)}
          role="presentation"
        >
          <div className="scope-modal" onClick={(e) => e.stopPropagation()} role="dialog">
            <h3>Esemény törlése</h3>
            <p>Biztosan törli: „{deleteConfirmEvent.title}”?</p>
            <div className="scope-modal-actions">
              <button
                type="button"
                className="scope-modal-btn scope-modal-btn--danger"
                onClick={async () => {
                  const ev = deleteConfirmEvent;
                  setDeleteConfirmEvent(null);
                  await executeDelete(ev, 'series');
                }}
              >
                Törlés
              </button>
              <button
                type="button"
                className="scope-modal-btn scope-modal-btn--ghost"
                onClick={() => setDeleteConfirmEvent(null)}
              >
                Mégse
              </button>
            </div>
          </div>
        </div>
      )}

      {showEventModal && (
        <div
          className="event-modal-overlay"
          onClick={() => setShowEventModal(false)}
          role="presentation"
        >
          <div className="event-modal event-modal--wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingEvent ? 'Esemény szerkesztése' : 'Új esemény'}</h3>
              <button
                type="button"
                onClick={() => {
                  setShowEventModal(false);
                  setEditingEvent(null);
                  setNewEvent(emptyEventForm());
                }}
              >
                ✕
              </button>
            </div>

            <form
              onSubmit={editingEvent ? handleUpdateEvent : handleAddEvent}
              className="event-form"
            >
              <div className="form-group">
                <label htmlFor="ev-title">Cím *</label>
                <input
                  id="ev-title"
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent((p) => ({ ...p, title: e.target.value }))}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="ev-type">Típus *</label>
                <select
                  id="ev-type"
                  value={newEvent.type}
                  onChange={(e) => setNewEvent((p) => ({ ...p, type: e.target.value }))}
                >
                  {ADMIN_EVENT_TYPES.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.icon} {type.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="ev-desc">Leírás</label>
                <textarea
                  id="ev-desc"
                  value={newEvent.description}
                  onChange={(e) => setNewEvent((p) => ({ ...p, description: e.target.value }))}
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label htmlFor="ev-rec">Ismétlés *</label>
                <select
                  id="ev-rec"
                  value={newEvent.recurrenceType}
                  onChange={(e) => {
                    const recurrenceType = e.target.value;
                    setNewEvent((prev) => ({
                      ...prev,
                      recurrenceType,
                      daysOfWeek:
                        usesWeekdayPicker(recurrenceType) && prev.daysOfWeek.length === 0
                          ? [getWeekdayFromDateStr(prev.date)]
                          : prev.daysOfWeek,
                      recurrenceEndDate:
                        recurrenceType !== 'once' && !prev.recurrenceEndDate
                          ? defaultRecurrenceEndDate()
                          : prev.recurrenceEndDate,
                    }));
                  }}
                  disabled={editingEvent && editScope === 'occurrence'}
                >
                  <option value="once">Egyszeri</option>
                  <option value="daily">Naponta</option>
                  <option value="weekly">Hetente</option>
                  <option value="biweekly">Kéthetente</option>
                  <option value="monthly">Havonta</option>
                </select>
              </div>

              {usesWeekdayPicker(newEvent.recurrenceType) && (
                <div className="form-group">
                  <label>Heti napok</label>
                  <div className="weekday-picker">
                    {WEEKDAY_OPTIONS.map((day) => (
                      <label key={day.id} className="weekday-chip">
                        <input
                          type="checkbox"
                          checked={newEvent.daysOfWeek.includes(day.id)}
                          onChange={() => toggleWeekday(day.id)}
                          disabled={editingEvent && editScope === 'occurrence'}
                        />
                        <span>{day.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {newEvent.recurrenceType !== 'once' && (
                <div className="form-group">
                  <label htmlFor="ev-rec-end">Ismétlődés vége (opcionális)</label>
                  <input
                    id="ev-rec-end"
                    type="date"
                    value={newEvent.recurrenceEndDate}
                    onChange={(e) =>
                      setNewEvent((p) => ({ ...p, recurrenceEndDate: e.target.value }))
                    }
                    disabled={editingEvent && editScope === 'occurrence'}
                  />
                  <small className="form-help">
                    Alapértelmezés: 3 hónap. Üresen legfeljebb 12 hónapig jelenik meg a naptárban.
                  </small>
                </div>
              )}

              {useMultiDateCreate && (
                <div className="form-group">
                  <label>Kijelölt napok ({selectedDates.length})</label>
                  <div className="selected-dates-chips">
                    {selectedDates.map((d) => (
                      <span key={d} className="selected-date-chip">
                        {formatDate(d)}
                      </span>
                    ))}
                  </div>
                  <small className="form-help">
                    Egyszeri esemény mindegyik napra külön kerül fel (ugyanaz a cím és idő).
                  </small>
                </div>
              )}

              <div className="form-row form-row--datetime">
                {!useMultiDateCreate && (
                  <div className="form-group">
                    <label htmlFor="ev-date">Dátum *</label>
                    <input
                      id="ev-date"
                      type="date"
                      value={newEvent.date}
                      onChange={(e) => handleEventDateChange(e.target.value)}
                      required
                      disabled={editingEvent && editScope === 'occurrence'}
                    />
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="ev-start">Kezdés *</label>
                  <input
                    id="ev-start"
                    type="text"
                    value={newEvent.startTime}
                    onChange={(e) => {
                      setNewEvent((p) => ({ ...p, startTime: e.target.value }));
                      e.target.setCustomValidity('');
                    }}
                    onInvalid={(e) => e.target.setCustomValidity('Adja meg az időpontot ÓÓ:PP formátumban (pl. 09:00).')}
                    placeholder="09:00"
                    pattern="^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="ev-end">Befejezés *</label>
                  <input
                    id="ev-end"
                    type="text"
                    value={newEvent.endTime}
                    onChange={(e) => {
                      setNewEvent((p) => ({ ...p, endTime: e.target.value }));
                      e.target.setCustomValidity('');
                    }}
                    onInvalid={(e) => e.target.setCustomValidity('Adja meg az időpontot ÓÓ:PP formátumban (pl. 10:00).')}
                    placeholder="10:00"
                    pattern="^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$"
                    required
                  />
                </div>
              </div>
              <p className="form-help form-help--block">
                Időtartam: minimum 15 perc, maximum 8 óra. Az esemény nem nyúlhat át éjfélen.
              </p>

              <div className="modal-actions modal-actions--split">
                {editingEvent && (
                  <button
                    type="button"
                    className="modal-delete-btn"
                    onClick={() => {
                      setShowEventModal(false);
                      requestDelete(editingEvent);
                    }}
                  >
                    Törlés
                  </button>
                )}
                <div className="modal-actions-right">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEventModal(false);
                      setEditingEvent(null);
                      setNewEvent(emptyEventForm());
                    }}
                  >
                    Mégse
                  </button>
                  <button type="submit" className="submit-btn" disabled={isLoading}>
                    {editingEvent
                      ? 'Mentés'
                      : useMultiDateCreate
                        ? `${selectedDates.length} napra hozzáadás`
                        : 'Hozzáadás'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Activity log modal */}
      {showLogModal && (
        <div
          className="log-modal-overlay"
          onClick={() => setShowLogModal(false)}
          role="presentation"
        >
          <div
            className="log-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Tevékenységnapló"
          >
            <div className="log-modal-header">
              <h3 className="log-modal-title">Tevékenységnapló</h3>
              <div className="log-modal-filters">
                <button
                  type="button"
                  className={`log-filter-btn ${logFilter === 'all' ? 'is-active' : ''}`}
                  onClick={() => setLogFilter('all')}
                >
                  Összes
                </button>
                <button
                  type="button"
                  className={`log-filter-btn ${logFilter === 'errors' ? 'is-active' : ''}`}
                  onClick={() => setLogFilter('errors')}
                >
                  Csak hibák
                </button>
              </div>
              <button
                type="button"
                className="log-modal-close"
                onClick={() => setShowLogModal(false)}
                aria-label="Bezárás"
              >
                ✕
              </button>
            </div>

            <div className="log-modal-body">
              {logLoading ? (
                <p className="log-empty">Betöltés…</p>
              ) : logEntries.length === 0 ? (
                <p className="log-empty">Még nincs bejegyzés.</p>
              ) : (() => {
                const filtered = logFilter === 'errors'
                  ? logEntries.filter((e) => e.status === 'error')
                  : logEntries;
                if (filtered.length === 0) {
                  return <p className="log-empty">Nincs hiba a naplóban.</p>;
                }
                return (
                  <div className="log-table-wrap">
                    <table className="log-table">
                      <thead>
                        <tr>
                          <th>Idő</th>
                          <th>Felhasználó</th>
                          <th>Művelet</th>
                          <th>Esemény</th>
                          <th>Dátum</th>
                          <th>Eredmény</th>
                          <th>Részletek</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((entry) => (
                          <tr key={entry._id} className={entry.status === 'error' ? 'log-row--error' : ''}>
                            <td className="log-cell-time">
                              {new Date(entry.createdAt).toLocaleString('hu-HU', {
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </td>
                            <td>{entry.actor?.nickname || entry.actor?.username || '—'}</td>
                            <td>{ACTION_LABELS[entry.action] || entry.action}</td>
                            <td>{entry.eventTitle || '—'}</td>
                            <td>{entry.eventDate || '—'}</td>
                            <td>
                              <span className={`log-status log-status--${entry.status}`}>
                                {entry.status === 'success' ? 'Siker' : 'Hiba'}
                              </span>
                            </td>
                            <td className="log-cell-detail">{entry.detail || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCalendarManager;
